/**

 * Competition status transitions & lifecycle (ADR-028 Management).

 * Business rules live here — not in React components.

 */



import { COMPETITION_STATUSES } from "./competitionTypes";



/** Canonical management lifecycle (Feature 1). Soft-delete is a side-exit. */

export const COMPETITION_LIFECYCLE = Object.freeze([

  COMPETITION_STATUSES.DRAFT,

  COMPETITION_STATUSES.PUBLISHED,

  COMPETITION_STATUSES.REGISTRATION_OPEN,

  COMPETITION_STATUSES.REGISTRATION_CLOSED,

  COMPETITION_STATUSES.IN_PROGRESS,

  COMPETITION_STATUSES.COMPLETED,

  COMPETITION_STATUSES.ARCHIVED,

  COMPETITION_STATUSES.DELETED,

]);



/** Statuses that may soft-delete (not draft — drafts are hard-deleted only). */

const SOFT_DELETE_FROM = Object.freeze([

  COMPETITION_STATUSES.PUBLISHED,

  COMPETITION_STATUSES.REGISTRATION_OPEN,

  COMPETITION_STATUSES.REGISTRATION_CLOSED,

  COMPETITION_STATUSES.IN_PROGRESS,

  COMPETITION_STATUSES.COMPLETED,

  COMPETITION_STATUSES.ARCHIVED,

  COMPETITION_STATUSES.DRAW_PUBLISHED,

]);



/** @type {Readonly<Record<string, string[]>>} */

export const ALLOWED_STATUS_TRANSITIONS = Object.freeze({

  [COMPETITION_STATUSES.DRAFT]: [COMPETITION_STATUSES.PUBLISHED],

  [COMPETITION_STATUSES.PUBLISHED]: [

    COMPETITION_STATUSES.REGISTRATION_OPEN,

    COMPETITION_STATUSES.REGISTRATION_CLOSED,

    COMPETITION_STATUSES.DELETED,

  ],

  [COMPETITION_STATUSES.REGISTRATION_OPEN]: [

    COMPETITION_STATUSES.REGISTRATION_CLOSED,

    COMPETITION_STATUSES.DELETED,

  ],

  [COMPETITION_STATUSES.REGISTRATION_CLOSED]: [

    COMPETITION_STATUSES.REGISTRATION_OPEN,

    COMPETITION_STATUSES.IN_PROGRESS,

    COMPETITION_STATUSES.DELETED,

  ],

  [COMPETITION_STATUSES.IN_PROGRESS]: [

    COMPETITION_STATUSES.COMPLETED,

    COMPETITION_STATUSES.DELETED,

  ],

  [COMPETITION_STATUSES.COMPLETED]: [

    COMPETITION_STATUSES.ARCHIVED,

    COMPETITION_STATUSES.DELETED,

  ],

  [COMPETITION_STATUSES.ARCHIVED]: [COMPETITION_STATUSES.DELETED],

  [COMPETITION_STATUSES.DELETED]: [],

  /** Legacy room-draw synonym of published */

  [COMPETITION_STATUSES.DRAW_PUBLISHED]: [

    COMPETITION_STATUSES.REGISTRATION_OPEN,

    COMPETITION_STATUSES.DELETED,

  ],

});



/**

 * Normalize legacy draw_published → published for management checks.

 * @param {string | null | undefined} status

 */

export function normalizeCompetitionStatus(status) {

  if (status === COMPETITION_STATUSES.DRAW_PUBLISHED) {

    return COMPETITION_STATUSES.PUBLISHED;

  }

  return status || COMPETITION_STATUSES.DRAFT;

}



/**

 * @param {string} fromStatus

 * @param {string} toStatus

 * @returns {{ ok: true } | { ok: false, error: string }}

 */

export function canTransitionCompetitionStatus(fromStatus, toStatus) {

  const from = normalizeCompetitionStatus(fromStatus);

  const to = normalizeCompetitionStatus(toStatus);

  if (from === to) {

    return { ok: true };

  }

  const allowed = ALLOWED_STATUS_TRANSITIONS[fromStatus] || ALLOWED_STATUS_TRANSITIONS[from] || [];

  if (!allowed.includes(to) && !allowed.includes(toStatus)) {

    return {

      ok: false,

      error: `Cannot move competition from “${from}” to “${to}”.`,

    };

  }

  return { ok: true };

}



/**

 * Extra lifecycle guards beyond the adjacency graph.

 * @param {object} competition

 * @param {string} toStatus

 * @param {number} [nowMs]

 */

export function validateStatusTransitionGuards(competition, toStatus, nowMs = Date.now()) {

  const from = normalizeCompetitionStatus(competition?.status);

  const to = normalizeCompetitionStatus(toStatus);



  if (to === COMPETITION_STATUSES.DELETED) {

    if (from === COMPETITION_STATUSES.DRAFT) {

      return {

        ok: false,

        error: "Draft competitions should be permanently deleted instead of soft-deleted.",

      };

    }

    if (from === COMPETITION_STATUSES.DELETED) {

      return { ok: false, error: "Competition is already deleted." };

    }

    if (!SOFT_DELETE_FROM.includes(from) && !SOFT_DELETE_FROM.includes(competition?.status)) {

      return { ok: false, error: "This competition cannot be soft-deleted." };

    }

    return { ok: true };

  }



  if (to === COMPETITION_STATUSES.ARCHIVED && from === COMPETITION_STATUSES.DRAFT) {

    return { ok: false, error: "Draft competitions cannot be archived. Delete the draft instead." };

  }

  if (to === COMPETITION_STATUSES.ARCHIVED && from !== COMPETITION_STATUSES.COMPLETED) {

    return { ok: false, error: "Only completed competitions can be archived." };

  }

  if (to === COMPETITION_STATUSES.COMPLETED) {

    const startMs = competition?.dates?.startMs ?? null;

    if (startMs != null && nowMs < startMs) {

      return { ok: false, error: "Cannot complete a competition before its start date." };

    }

    if (from === COMPETITION_STATUSES.DRAFT) {

      return { ok: false, error: "Cannot complete a draft competition." };

    }

  }

  if (to === COMPETITION_STATUSES.PUBLISHED && from !== COMPETITION_STATUSES.DRAFT) {

    return { ok: false, error: "Only drafts can be published." };

  }

  return canTransitionCompetitionStatus(competition?.status, toStatus);

}



/**

 * Soft-deleted or archived competitions are read-only for organizers.

 * @param {string | null | undefined} status

 */

export function isCompetitionReadOnly(status) {

  const s = normalizeCompetitionStatus(status);

  return s === COMPETITION_STATUSES.ARCHIVED || s === COMPETITION_STATUSES.DELETED;

}



/**

 * @param {string | null | undefined} status

 */

export function isCompetitionSoftDeleted(status) {

  return normalizeCompetitionStatus(status) === COMPETITION_STATUSES.DELETED;

}



/**

 * @param {string | null | undefined} status

 */

export function isCompetitionDraft(status) {

  return normalizeCompetitionStatus(status) === COMPETITION_STATUSES.DRAFT;

}



/**

 * Human label for badges.

 * @param {string | null | undefined} status

 */

export function competitionStatusLabel(status) {

  const s = normalizeCompetitionStatus(status);

  const labels = {

    [COMPETITION_STATUSES.DRAFT]: "Draft",

    [COMPETITION_STATUSES.PUBLISHED]: "Published",

    [COMPETITION_STATUSES.REGISTRATION_OPEN]: "Registration open",

    [COMPETITION_STATUSES.REGISTRATION_CLOSED]: "Registration closed",

    [COMPETITION_STATUSES.IN_PROGRESS]: "In progress",

    [COMPETITION_STATUSES.COMPLETED]: "Completed",

    [COMPETITION_STATUSES.ARCHIVED]: "Archived",

    [COMPETITION_STATUSES.DELETED]: "Deleted",

  };

  return labels[s] || s || "Unknown";

}



/**

 * TRABadge tone for status.

 * @param {string | null | undefined} status

 */

export function competitionStatusTone(status) {

  const s = normalizeCompetitionStatus(status);

  if (s === COMPETITION_STATUSES.DRAFT) return "default";

  if (s === COMPETITION_STATUSES.PUBLISHED || s === COMPETITION_STATUSES.REGISTRATION_OPEN) {

    return "info";

  }

  if (s === COMPETITION_STATUSES.IN_PROGRESS) return "warning";

  if (s === COMPETITION_STATUSES.COMPLETED) return "success";

  if (s === COMPETITION_STATUSES.ARCHIVED || s === COMPETITION_STATUSES.DELETED) return "default";

  return "default";

}


